import type { ReactNode } from 'react'
import {
  Dialog as AriaDialog,
  Heading,
  Modal,
  ModalOverlay,
  Text,
} from 'react-aria-components'
import {
  cva,
  type VariantProps,
} from 'class-variance-authority'
import { X } from 'lucide-react'

import { Button } from './button'
import { cn } from '@/shared/lib/cn'

const dialogSizeVariants = cva(
  'w-full',
  {
    variants: {
      size: {
        small: 'max-w-md',
        medium: 'max-w-xl',
        large: 'max-w-3xl',
        extraLarge: 'max-w-5xl',
      },
    },

    defaultVariants: {
      size: 'medium',
    },
  },
)

interface DialogRenderContext {
  close: () => void
}

export interface DialogProps
  extends VariantProps<
    typeof dialogSizeVariants
  > {
  isOpen: boolean

  onOpenChange: (
    isOpen: boolean,
  ) => void

  title: ReactNode
  description?: ReactNode

  children:
    | ReactNode
    | ((
        context: DialogRenderContext,
      ) => ReactNode)

  footer?:
    | ReactNode
    | ((
        context: DialogRenderContext,
      ) => ReactNode)

  role?: 'dialog' | 'alertdialog'

  isDismissable?: boolean
  isKeyboardDismissDisabled?: boolean
  showCloseButton?: boolean

  className?: string
  contentClassName?: string
}

export function Dialog({
  isOpen,
  onOpenChange,
  title,
  description,
  children,
  footer,
  role = 'dialog',
  size = 'medium',
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  showCloseButton = true,
  className,
  contentClassName,
}: DialogProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={
        isKeyboardDismissDisabled
      }
      className={[
        'fixed inset-0 z-50',
        'flex items-center justify-center',
        'overflow-y-auto p-4',
        'bg-slate-950/60 backdrop-blur-sm',
      ].join(' ')}
    >
      <Modal
        className={cn(
          dialogSizeVariants({ size }),
          className,
        )}
      >
        <AriaDialog
          role={role}
          className={[
            'max-h-[calc(100vh-2rem)]',
            'overflow-hidden rounded-xl',
            'border border-(--color-border)',
            'bg-(--color-surface)',
            'text-(--color-foreground)',
            'shadow-2xl outline-none',
          ].join(' ')}
        >
          {({ close }) => {
            const context = { close }

            return (
              <div className="flex max-h-[calc(100vh-2rem)] flex-col">
                <div className="flex shrink-0 items-start gap-4 border-b border-(--color-border) px-6 py-5">
                  <div className="min-w-0 flex-1">
                    <Heading
                      slot="title"
                      className="text-lg font-semibold"
                    >
                      {title}
                    </Heading>

                    {description ? (
                      <Text
                        slot="description"
                        className="mt-1 block text-sm leading-6 text-(--color-muted-foreground)"
                      >
                        {description}
                      </Text>
                    ) : null}
                  </div>

                  {showCloseButton ? (
                    <Button
                      variant="ghost"
                      size="small"
                      className="h-9 w-9 shrink-0 p-0"
                      aria-label="Close dialog"
                      onPress={close}
                    >
                      <X
                        aria-hidden="true"
                        className="h-5 w-5"
                      />
                    </Button>
                  ) : null}
                </div>

                <div
                  className={cn(
                    'min-h-0 flex-1 overflow-y-auto px-6 py-5',
                    contentClassName,
                  )}
                >
                  {typeof children === 'function'
                    ? children(context)
                    : children}
                </div>

                {footer ? (
                  <div className="flex shrink-0 items-center justify-end gap-3 border-t border-(--color-border) px-6 py-4">
                    {typeof footer === 'function'
                      ? footer(context)
                      : footer}
                  </div>
                ) : null}
              </div>
            )
          }}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  )
}